/**
 * Mobile Navigation Wrapper — OMN-52
 *
 * Renders the sidebar as a hamburger-triggered drawer on mobile (<1024px)
 * and as a fixed sidebar on larger screens. The 1024px line matches the
 * Bookings calendar's desktop/mobile switch so iPad portrait is consistently
 * "mobile" across nav and content.
 */

import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Sidebar } from './Sidebar';

export const MobileNav: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Close drawer when window resizes to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  // Close drawer when clicking a nav link (detected via Escape key or outside click)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  return (
    <>
      {/* Mobile hamburger button (visible <lg) */}
      <button
        type="button"
        className="lg:hidden fixed top-3 left-3 z-50 p-2 rounded-md bg-white shadow border border-gray-200 text-gray-700 hover:bg-gray-50"
        onClick={() => setIsOpen(true)}
        aria-label="Open navigation menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Desktop sidebar (visible lg+) */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {/* Mobile drawer */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-40"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          {/* Drawer */}
          <div className="lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <span className="font-semibold text-gray-900">Menu</span>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-1 rounded text-gray-500 hover:text-gray-700"
                aria-label="Close navigation menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div onClick={() => setIsOpen(false)}>
              <Sidebar />
            </div>
          </div>
        </>
      )}
    </>
  );
};
